'use client'

import { useEffect, useRef } from 'react'

import { clsx } from 'clsx'

import { YesIcon } from '@dfds-frontend/navigator-icons/icons'
import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'

import { BookingSummaryStepNumber } from '@/app/[locale]/[ferry]/(booking-summary)/BookingSummaryStepNumber'
import { isFootPassenger } from '@/app/lib/vehicleHelpers'
import { VehicleInformation } from '@/components/stepper/summary/steps/VehicleInformation'
import AdministrationFee from '@/components/summary/AdministrationFee'
import SummaryLeg from '@/components/summary/Leg'
import { OfferInfo } from '@/components/summary/OfferInfo'
import PriceItem from '@/components/summary/PriceItem'
import SurchargeRow from '@/components/summary/Surcharge'

import { toAddonResource } from '@/utils/addonHelpers'

import { useTranslation } from '@/hooks/useDebugTranslations'
import { useVehicleDimensions, type VehicleDimensions } from '@/hooks/useVehicleDimensions'

import { NavigateButton } from '../NavigationButton'
import { Step } from '../steps'
import { BillingInformation } from './steps/BillingInformation'
import Passengers from './steps/Passengers'
import { PaymentInformation } from './steps/PaymentInformation'
import TravelInfo from './steps/TravelInfo'
import { isStepInactive, ToPassengerCategory } from './SummaryUtils'

export const SummaryContentDesktop: React.FC<{
  basket: BffInterfacesBasket.Basket
  currentIndex: number
  petCount?: number
  routes?: { outbound: string; homebound: string | null }
  scrollToCurrentStep?: boolean
  steps: Step[]
  dimensionsByCode?: Record<string, VehicleDimensions>
  offerDescription?: string
}> = ({ basket, currentIndex, dimensionsByCode, offerDescription, petCount, routes, scrollToCurrentStep = true, steps }) => {
  const { t } = useTranslation()

  const hideOffer = (departures: BffInterfacesBasket.Departure[]): boolean => {
    return departures.filter((d) => d.offer && d.offer.hidePriceAdjustment).length > 0
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const currentStepRef = useRef<HTMLDivElement>(null)
  const offer = basket ? basket.departures.find((d) => d.offer)?.offer : undefined
  useEffect(() => {
    if (scrollToCurrentStep && currentStepRef.current && containerRef.current && currentIndex > 1) {
      currentStepRef.current.scrollIntoView({
        block: 'center',
      })
    }
  }, [currentIndex, scrollToCurrentStep])

  const isCurrentStepAfterExtras = currentIndex >= steps.findIndex((s) => s.stepType === 'EXTRAS') + 1
  const isCurrentStepAfterPets = currentIndex >= steps.findIndex((s) => s.stepType === 'PETS') + 1

  const loadedVehicleDimensions = useVehicleDimensions(
    !dimensionsByCode && basket.vehicle?.vehicleTypeCode && !isFootPassenger(basket.vehicle.vehicleTypeCode)
      ? basket.vehicle.vehicleTypeCode
      : undefined
  )
  const loadedTrailerDimensions = useVehicleDimensions(dimensionsByCode ? undefined : basket.vehicle?.trailerTypeCode)
  const vehicleDimensions = dimensionsByCode
    ? { dimensions: dimensionsByCode[basket.vehicle?.vehicleTypeCode ?? ''], loading: false }
    : loadedVehicleDimensions
  const trailerDimensions = dimensionsByCode
    ? { dimensions: dimensionsByCode[basket.vehicle?.trailerTypeCode ?? ''], loading: false }
    : loadedTrailerDimensions

  return (
    <div className="relative flex h-full flex-col overflow-y-auto" data-e2e="stepper-summary" ref={containerRef}>
      {offer && !offer.trackingOffer ? (
        <div className="px-semantic-global-lg pt-semantic-global-lg">
          <OfferInfo offer={offer} description={offerDescription} />
        </div>
      ) : null}
      <div className={clsx('flex flex-col', offer && !offer.trackingOffer && '[&>div:first-child]:pt-semantic-global-md')}>
        {steps.map((s, i) => {
          if (s.stepType === 'CONFIRMATION') return null
          const stepInactive = isStepInactive(s.stepType, currentIndex, i, basket)

          return (
            <div
              className={clsx(
                'pt-semantic-global-xl pb-semantic-global-lg px-semantic-global-lg gap-semantic-global-lg text-semantic-global-text-default flex flex-col',
                currentIndex === i && 'border-semantic-global-border-light bg-semantic-global-background-light-sky-blue border-b',
                !stepInactive && 'border-semantic-global-border-light border-b'
              )}
              key={i}
              ref={currentIndex === i ? currentStepRef : null}
            >
              <div className="gap-primitive-8 flex w-full flex-row items-center justify-between">
                <div className={clsx('typography-heading-sm gap-semantic-global-sm flex items-center text-left')}>
                  <span aria-hidden="true" className="flex size-6 shrink-0 items-center justify-center">
                    {!stepInactive ? (
                      <span className="bg-semantic-global-success-default text-semantic-global-background-white flex size-6 items-center justify-center rounded-full">
                        <YesIcon size="md" />
                      </span>
                    ) : (
                      <span data-e2e={`step-type-${s.stepType?.toLowerCase()}`}>
                        <BookingSummaryStepNumber current={currentIndex === i} number={i + 1} />
                      </span>
                    )}
                  </span>
                  {t(s.resourceKey)}
                </div>

                {!stepInactive && s.stepType !== 'TRAVEL' && s.stepType !== 'REVIEW' ? (
                  <NavigateButton isVertical={false} step={s} />
                ) : null}
              </div>

              {!stepInactive || currentIndex === i ? (
                <div>
                  {s.stepType === 'TRAVEL' ? (
                    <div className="w-full" data-e2e="travel-information">
                      <TravelInfo
                        petCount={petCount}
                        routes={routes}
                        categories={ToPassengerCategory(basket.passengers)}
                        trailerCode={basket.vehicle?.trailerTypeCode}
                        trailerDimensions={trailerDimensions}
                        vehicleCode={basket.vehicle?.vehicleTypeCode}
                        vehicleDimensions={vehicleDimensions}
                      />
                    </div>
                  ) : null}

                  {s.stepType === 'DEPARTURES' ? (
                    <div className="gap-semantic-global-lg flex w-full flex-col justify-center" data-e2e="departure-information">
                      <SummaryLeg
                        routes={routes}
                        currencyCode={basket.priceStructure.currency}
                        key="outbound-summary"
                        type="outbound"
                        {...basket.departures[0]}
                        hideOfferPrice={hideOffer(basket.departures)}
                        offerPrice={
                          !offer?.trackingOffer && !offer?.addonOffer && basket.departures[0]?.offer
                            ? basket.departures[0]?.offer?.price
                            : undefined
                        }
                        ticketPrice={basket.departures[0]?.ticketPrice}
                      />

                      {basket.departures.length > 1 && basket.departures[1] ? (
                        <SummaryLeg
                          routes={routes}
                          currencyCode={basket.priceStructure.currency}
                          key="return-summary"
                          type="return"
                          {...basket.departures[1]}
                          hideOfferPrice={hideOffer(basket.departures)}
                          offerPrice={
                            !offer?.trackingOffer && !offer?.addonOffer && basket.departures[1]?.offer
                              ? basket.departures[1]?.offer?.price
                              : undefined
                          }
                          ticketPrice={basket.departures[1]?.ticketPrice}
                        />
                      ) : null}
                      <SurchargeRow
                        currency={basket?.priceStructure?.currency}
                        homebound={basket.departures[1]}
                        outbound={basket.departures[0]}
                      />
                      <AdministrationFee currency={basket?.priceStructure?.currency} price={basket.priceStructure?.administrationFee} />
                    </div>
                  ) : null}

                  {s.stepType === 'PETS' ? (
                    <div className="gap-semantic-global-2xs flex min-h-[20px] w-full flex-col justify-center" data-e2e="pets-information">
                      {basket.departures[0]?.pets.map((pet, i) => {
                        const returnPet = basket.departures[1]?.pets.find((p) => p.addonCode === pet.addonCode)
                        // Calculate total price from both legs
                        const outboundPrice = pet.unitPriceStructure.basePrice * pet.quantity
                        const returnPrice = returnPet ? returnPet.unitPriceStructure.basePrice * returnPet.quantity : 0
                        const totalPrice = outboundPrice + returnPrice

                        return (
                          <PriceItem
                            currency={basket.priceStructure.currency}
                            description={`${pet.quantity} x ${t(toAddonResource(pet.parentAddonCode, pet.addonCode, pet.typeCode))}`}
                            endToEndDescrAttr="bookingSummaryPetDescription"
                            endToEndPriceAttr="bookingSummaryPetPriceExpanded"
                            key={i}
                            price={totalPrice}
                          />
                        )
                      })}

                      {basket.departures[0]?.pets.length === 0 && (!basket.departures[1] || basket.departures[1]?.pets.length === 0) ? (
                        <div className="typography-body-md-regular lg:typography-body-md-regular text-semantic-global-text-default text-left">
                          {isCurrentStepAfterPets ? t('NO-PETS-SELECTED') : t('SELECT-PETS')}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {s.stepType === 'EXTRAS' ? (
                    <div className="gap-semantic-global-2xs flex min-h-[20px] w-full flex-col justify-center" data-e2e="addon-information">
                      {basket.departures[0]?.addons.length > 0 ? (
                        <div className={clsx('flex flex-col justify-center')}>
                          <div className="flex">
                            <div
                              className="typography-body-sm-medium lg:typography-body-md-medium text-semantic-global-info-darker"
                              data-e2e="bookingSummaryRouteInfo"
                            >
                              {t('OUTBOUND')}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {basket.departures[0]?.addons.length === 0 ? (
                        <div className="typography-body-md-regular lg:typography-body-md-regular text-semantic-global-text-light text-left">
                          {isCurrentStepAfterExtras ? t('NO-EXTRAS-SELECTED') : t('SELECT-EXTRAS')}
                        </div>
                      ) : null}
                      <div data-e2e="outbound-addons">
                        {basket.departures[0]?.addons.map((addon, i) => (
                          <PriceItem
                            currency={basket.priceStructure.currency}
                            description={`${addon.quantity} x ${t(
                              toAddonResource(addon.parentAddonCode, addon.addonCode, addon.typeCode)
                            )}`}
                            endToEndDescrAttr="bookingSummaryAddonDescription"
                            endToEndPriceAttr="bookingSummaryAddonPriceExpanded"
                            key={i}
                            offerPrice={addon.unitPriceStructure.offer ? addon.unitPriceStructure.offer?.price * addon.quantity : undefined}
                            price={addon.unitPriceStructure.basePrice * addon.quantity}
                          />
                        ))}
                      </div>

                      {basket.departures[1] && basket.departures[1]?.addons.length > 0 ? (
                        <div className={clsx('mt-primitive-16 gap-semantic-global-xs flex flex-col justify-center')}>
                          <div className="flex">
                            <div
                              className="typography-body-sm-medium lg:typography-body-md-medium text-semantic-global-info-darker"
                              data-e2e="bookingSummaryRouteInfo"
                            >
                              {t('RETURN')}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div data-e2e="return-addons">
                        {basket.departures[1]
                          ? basket.departures[1]?.addons.map((addon, i) => (
                              <PriceItem
                                currency={basket.priceStructure.currency}
                                description={`${addon.quantity} x ${t(
                                  toAddonResource(addon.parentAddonCode, addon.addonCode, addon.typeCode)
                                )}`}
                                endToEndDescrAttr="bookingSummaryAddonDescription"
                                endToEndPriceAttr="bookingSummaryAddonPriceExpanded"
                                key={i}
                                offerPrice={
                                  addon.unitPriceStructure.offer ? addon.unitPriceStructure.offer?.price * addon.quantity : undefined
                                }
                                price={addon.unitPriceStructure.basePrice * addon.quantity}
                              />
                            ))
                          : null}
                      </div>
                    </div>
                  ) : null}

                  {s.stepType === 'PASSENGERS' ? (
                    <div className="gap-primitive-16 flex min-h-[20px] w-full flex-col justify-center" data-e2e="passenger-information">
                      <Passengers
                        currency={basket.priceStructure.currency}
                        outboundPassengerDepartureData={basket.departures[0]?.passengerDepartureData}
                        returnPassengerDepartureData={basket.departures[1]?.passengerDepartureData}
                        passengers={basket.passengers}
                      />
                    </div>
                  ) : null}

                  {s.stepType === 'CHECKOUT' ? (
                    <div className="gap-primitive-16 flex min-h-[20px] w-full flex-col justify-center" data-e2e="checkout-information">
                      {basket.vehicle && !isFootPassenger(basket.vehicle.vehicleTypeCode) ? (
                        <VehicleInformation vehicle={basket.vehicle} dimensionsByCode={dimensionsByCode} />
                      ) : null}
                      <BillingInformation customer={basket.customer} t={t} />
                      <PaymentInformation paymentMethod={basket.paymentMethod} t={t} />
                    </div>
                  ) : null}

                  {s.stepType === 'REVIEW' ? (
                    <div className="w-full" data-e2e="review-information">
                      <div className="typography-body-md-regular lg:typography-body-md-regular text-semantic-global-text-light text-left">
                        {t('REVIEW-BOOKING-MESSAGE')}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
